import React from "react";
import { toast } from "react-toastify";
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import {
  AccountBoxOutlined,
  AddAPhotoOutlined,
  CategoryOutlined,
  EmailOutlined,
  PhoneOutlined,
} from "@mui/icons-material";

import { useUserProfileContext } from "./UserProfileScreen";
import InformationTile from "components/Shared/InformationTile";
import ReportTable from "components/Shared/ReportTable";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";

import { useEditUserProfileMutation } from "store/services/userApi";
import { useAuth, useDOUploadFile } from "hooks";
import { setUser } from "store/authSlice";
import { useDispatch } from "react-redux";

const BasicProfileDetail = () => {
  const { loadingUserProfile, userProfile } = useUserProfileContext();

  return !loadingUserProfile ? (
    <Box padding={3}>
      <Grid container spacing={4}>
        {/* Left Column: Avatar and Name */}
        <Grid item xs={3}>
          <div className="flex flex-col items-center gap-2">
            <UpdateAvatar avatar={userProfile?.avatar?.url} />
            <Typography variant="h6"> {userProfile?.name} </Typography>
          </div>
        </Grid>

        {/* Right Column: Additional Details */}
        <Grid item xs={9}>
          <Grid container spacing={4}>
            <Grid item xs={12} />
            <Grid item xs={4}>
              <InformationTile
                icon={() => <EmailOutlined color="primary" />}
                title="Email"
              >
                {userProfile?.email ? (
                  <a
                    href={`mailto:${userProfile?.email}`}
                    className="custom-link"
                  >
                    {userProfile?.email}
                  </a>
                ) : (
                  "N/A"
                )}
              </InformationTile>
            </Grid>

            <Grid item xs={4}>
              <InformationTile
                icon={() => <CategoryOutlined color="primary" />}
                title="Category"
                subtitle={userProfile?.category}
              />
            </Grid>

            <Grid item xs={4}>
              <InformationTile
                icon={() => <PhoneOutlined color="primary" />}
                title="Phone"
                subtitle={userProfile?.phone}
              />
            </Grid>

            <Grid item xs={4}>
              <InformationTile
                icon={() => <AccountBoxOutlined color="primary" />}
                title="Designation"
                subtitle={userProfile?.designation}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Entities & Access Table */}
        <Grid item md={12}>
          <InformationTile title="Entities & Access">
            <ReportTable className="mt-2" disable100Vh>
              <ReportTable.Row>
                <ReportTable.Heading></ReportTable.Heading>
                <ReportTable.Heading>Name</ReportTable.Heading>
                <ReportTable.Heading>Account</ReportTable.Heading>
                <ReportTable.Heading>Type</ReportTable.Heading>
                <ReportTable.Heading>Roles</ReportTable.Heading>
                <ReportTable.Heading>Status</ReportTable.Heading>
              </ReportTable.Row>

              <ReportTable.Body>
                {userProfile?.entities?.map((entity, index) => (
                  <ReportTable.Row key={entity._id}>
                    <ReportTable.Data> {index + 1}. </ReportTable.Data>
                    <ReportTable.Data>
                      {entity?.entity?.name}
                    </ReportTable.Data>
                    <ReportTable.Data className="text-center">
                      {entity?.entity?.account?.name}
                    </ReportTable.Data>
                    <ReportTable.Data className="text-center">
                      {entity?.userType}
                    </ReportTable.Data>
                    <ReportTable.Data>
                      <ul>
                        {entity?.authorizations.map((authorization) => (
                          <li
                            key={authorization._id}
                            className="ml-4 list-disc"
                          >
                            {authorization.role}
                          </li>
                        ))}
                      </ul>
                    </ReportTable.Data>
                    <ReportTable.Data className="text-center">
                      {entity?.isActive ? "Active" : "Inactive"}
                    </ReportTable.Data>
                  </ReportTable.Row>
                ))}
              </ReportTable.Body>
            </ReportTable>
          </InformationTile>
        </Grid>
      </Grid>
    </Box>
  ) : (
    <InfoGridSkeleton />
  );
};

export default BasicProfileDetail;

const UpdateAvatar = ({ avatar = null }) => {
  const [avatarPreview, setAvatarPreview] = React.useState(avatar);
  const fileInputRef = React.useRef(null);
  const { userProfile } = useUserProfileContext();
  const { loggedInUser, viewEntity } = useAuth();
  const { handleUploadFiles, uploadingFiles } = useDOUploadFile();
  const dispatch = useDispatch();

  const NAME_INITIAL = userProfile?.name
    ?.split(" ")
    ?.slice(0, 2)
    ?.map((el) => el?.charAt(0)?.toUpperCase())
    ?.join(" ");

  const [updateProfile, { isLoading: updatingProfile }] =
    useEditUserProfileMutation();

  const handleSelectImage = async (e) => {
    const imageFile = e.target.files[0];

    try {
      const { uploadedFiles } = await handleUploadFiles([imageFile], {
        folderName: "USER_AVATARS",
        entity: viewEntity.entity._id,
      });

      if (!uploadedFiles || uploadedFiles.length === 0) {
        throw new Error("Failed to upload image.");
      }

      const uploadedAvatar = uploadedFiles[0]; // Get the first uploaded file

      setAvatarPreview(uploadedAvatar?.url);

      const updatedProfile = await updateProfile({
        id: userProfile?._id,
        avatar: uploadedAvatar,
      }).unwrap();

      dispatch(
        setUser({
          ...loggedInUser,
          avatar: updatedProfile?.avatar,
        })
      );

      toast.success("Avatar successfully updated!");
    } catch (error) {
      console.error("Error uploading avatar:", error);
      toast.error(
        error?.data?.message ?? error?.message ?? "Couldn't Upload Avatar!"
      );
    }
  };

  return (
    <div className="group relative h-32 w-32 overflow-hidden rounded-full border-2 p-1">
      <div className="flex h-full w-full items-center justify-center rounded-full bg-blue-50 text-3xl font-medium text-blue-500 dark:bg-transparent dark:text-blue-200">
        {!avatarPreview ? (
          NAME_INITIAL
        ) : (
          <img
            src={avatarPreview}
            alt="Avatar"
            className="h-full w-full rounded-full object-cover"
          />
        )}
      </div>

      {(uploadingFiles || updatingProfile) ? (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200 bg-opacity-30 backdrop-blur-sm dark:bg-gray-900 dark:bg-opacity-40">
          <CircularProgress size={26} />
        </div>
      ) : (
        <div className="absolute inset-0 flex translate-y-full items-center justify-center rounded-full bg-gray-200 bg-opacity-30 transition-transform duration-200 ease-in group-hover:translate-y-0 dark:bg-gray-900 dark:bg-opacity-40">
          <IconButton onClick={() => fileInputRef?.current?.click()}>
            <AddAPhotoOutlined />
          </IconButton>
        </div>
      )}

      <input
        ref={fileInputRef}
        accept="image/*"
        type="file"
        className="hidden"
        onChange={handleSelectImage}
      />
    </div>
  );
};
